/**
 * userId.js — Public-facing user ID generation for Omnisio Workout
 *
 * IDs follow the pattern:  OMW-XXXXXX  (workout-native users)
 *                          OMN-XXXXXX  (imported from Omnisio Longevity via SSO)
 *
 * Alphabet: Crockford-base32-inspired, removing visually ambiguous chars:
 *   Removed: 0 (looks like O), O (looks like 0), 1 (looks like I/l),
 *            I (looks like 1/l), L (looks like 1/I)
 *   Remaining: 31 characters — enough for 31^6 ≈ 887 million unique IDs
 *              before a collision becomes likely. In practice, with birthday
 *              paradox, 50% collision probability hits at ~42k IDs (still
 *              acceptable for MVP; add a DB uniqueness check before persisting).
 */

// 31 unambiguous uppercase alphanumeric characters (no 0/O/1/I/L)
const ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
const SUFFIX_LENGTH = 6;

// Regex pattern for validation — kept in sync with ALPHABET above
// Excluded from A-Z: I (between H and J), L (between K and M), O (between N and P)
// So valid ranges are: A-H, J-K, M-N, P-Z  (plus digits 2-9)
const PUBLIC_ID_REGEX = /^OM[WN]-[2-9A-HJ-KM-NP-Z]{6}$/;

/**
 * Generate a new public ID for a user.
 *
 * @param {'workout'|'longevity'} source  - 'workout' → OMW- prefix,
 *                                          'longevity' → OMN- prefix (SSO import)
 * @returns {string}  e.g. "OMW-AB3M7Q"
 *
 * NOTE: Uniqueness is probabilistic (31^6 ≈ 887M space). Always verify
 * against the DB before persisting — the DB column should have a UNIQUE
 * constraint on `public_id`.
 */
export function generatePublicId(source = 'workout') {
  const prefix = source === 'longevity' ? 'OMN' : 'OMW';
  let suffix = '';
  for (let i = 0; i < SUFFIX_LENGTH; i++) {
    // Math.random() gives ~52 bits of entropy in V8, more than enough
    suffix += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
  }
  return `${prefix}-${suffix}`;
}

/**
 * Parse a public ID into its constituent parts.
 *
 * @param {string} id
 * @returns {{ prefix: 'OMW'|'OMN', suffix: string } | null}
 */
export function parsePublicId(id) {
  if (!isValidPublicId(id)) return null;
  const [prefix, suffix] = id.split('-');
  return { prefix, suffix };
}

/**
 * Validate a public ID string.
 *
 * @param {string} id
 * @returns {boolean}
 */
export function isValidPublicId(id) {
  if (typeof id !== 'string') return false;
  return PUBLIC_ID_REGEX.test(id);
}

/**
 * Determine the ID source from the auth method used in AuthContext.
 *
 * @param {string|null} authMethod  - Value from AuthContext.authMethod
 * @returns {'workout'|'longevity'}
 */
export function sourceFromAuthMethod(authMethod) {
  return authMethod === 'longevity_id' ? 'longevity' : 'workout';
}
